/* eslint-env browser */
// Customer / driver details › Info (wave 3, W2_Portal_BookingDetails look):
// the photo with the account facts (member since, app, last active), then
// cards of label / value rows: the profile, and contact invitations.
// Also exports UserSummaryCard, the strip at the top of both details pages
// (avatar, name, phone, status chips), so the two pages look the same.
//
// Data, exactly as before: the user record handed in by the details page
// (its one-user listener keeps it live), the staff list already loaded
// (fetchUsersOnce, for the "onboarded by" name) and, for admin and call-centre
// supervisors with critical edits on, the person's current login code
// (fetchOtpAuthRequests). Phone, email, TIN, ID number and the login code are
// hidden in demo mode, as before.
// Call-centre feedback (29 Oct 2026): a copy button beside the phone number
// (in the strip and in Profile) copies it without spaces (+251964540448)
// for MicroSIP; it is not there while numbers are hidden.
import React, { useEffect, useMemo } from 'react';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Skeleton from '@mui/material/Skeleton';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import { tokens as c, toneChipSx } from '../theme';
import { formatAddisDate, formatAddisDateTime } from '../common/exportUtils';
import { CopyButton, DetailCard, InfoRow, StatusChip, calmCaps, formatPhoneDisplay } from '../components/modern';

// Compact relative time — "2 hours ago", "3 days ago", or the Addis Ababa
// date for anything older than 30 days. Words come from the language packs
// (with English defaults), so Amharic, Oromo and Tigrinya staff read them in
// their language.
const formatTimeAgo = (t, ts) => {
  if (!ts) return '';
  const ms = parseInt(ts, 10);
  if (!Number.isFinite(ms)) return '';
  const diff = Date.now() - ms;
  if (diff < 60_000) return t('time_just_now', { defaultValue: 'just now' });
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) {
    return t('minutes_ago', { count: mins, defaultValue_one: '{{count}} minute ago', defaultValue_other: '{{count}} minutes ago', defaultValue: '{{count}} minutes ago' });
  }
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) {
    return t('time_hours_ago', { count: hrs, defaultValue_one: '{{count}} hour ago', defaultValue_other: '{{count}} hours ago', defaultValue: '{{count}} hours ago' });
  }
  const days = Math.floor(hrs / 24);
  if (days < 30) {
    return t('time_days_ago', { count: days, defaultValue_one: '{{count}} day ago', defaultValue_other: '{{count}} days ago', defaultValue: '{{count}} days ago' });
  }
  return formatAddisDate(ms);
};

const initialsOf = (u) =>
  [u && u.firstName, u && u.lastName]
    .map((n) => String(n || '').trim().charAt(0))
    .join('')
    .toUpperCase();

const nameOf = (u) => `${(u && u.firstName) || ''} ${(u && u.lastName) || ''}`.trim();

/**
 * The strip at the top of the customer and driver details pages: avatar,
 * name, phone (hidden in demo mode) and the account's status as chips
 * (role, approval; for a driver also online / on a trip). `action` goes on
 * the right (the Refresh button).
 */
export function UserSummaryCard({ user, action }) {
  const { t } = useTranslation();
  const settings = useSelector((state) => state.settingsdata.settings);
  const allowPrivate = !!(settings && settings.AllowCriticalEditsAdmin);
  const u = user || {};
  const L = (key, dflt) => t(key, { defaultValue: dflt });
  const isDriver = u.usertype === 'driver';
  const name = nameOf(u);
  const online = !!u.driverActiveStatus;

  // The page passes [] until it has the person: the strip's shape in grey.
  if (!u.id) {
    return (
      <Paper role="status" aria-label={L('loading', 'Loading…')} sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Skeleton variant="circular" width={56} height={56} aria-hidden="true" />
        <Box sx={{ flex: '1 1 240px', display: 'flex', flexDirection: 'column', gap: 1 }} aria-hidden="true">
          <Skeleton variant="rounded" width={200} height={20} />
          <Skeleton variant="rounded" width={140} height={14} />
        </Box>
        {action ? <Box sx={{ marginInlineStart: 'auto' }}>{action}</Box> : null}
      </Paper>
    );
  }

  const approval = isDriver
    ? {
        tone: u.approved ? 'success' : 'warning',
        icon: u.approved ? <CheckRoundedIcon /> : <ScheduleRoundedIcon />,
        label: u.approved ? L('drivers_approved', 'Approved') : L('drivers_waiting', 'Waiting'),
      }
    : {
        tone: u.approved ? 'success' : 'neutral',
        icon: u.approved ? <CheckRoundedIcon /> : <BlockRoundedIcon />,
        label: u.approved ? L('approved', 'Approved') : calmCaps(L('not_approved', 'Not approved')),
      };

  return (
    <Paper sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
      {u.profile_image ? (
        <Avatar src={u.profile_image} alt="" sx={{ width: 56, height: 56 }} />
      ) : (
        <Avatar aria-hidden="true" sx={{ width: 56, height: 56, bgcolor: c.SURFACE_ALT, color: c.INK_2, fontSize: 20, fontWeight: 700 }}>
          {initialsOf(u) || <PersonOutlineRoundedIcon />}
        </Avatar>
      )}
      <Box sx={{ minWidth: 0, flex: '1 1 240px' }}>
        <Typography component="h2" sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.4, color: c.INK, overflowWrap: 'anywhere' }}>
          {name || L('no_name', 'No name')}
        </Typography>
        <Typography component="p" sx={{ display: 'flex', alignItems: 'center', gap: 0.25, fontSize: 14.5, lineHeight: 1.5, color: c.INK_2, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>
          {allowPrivate ? formatPhoneDisplay(u.mobile) || '—' : t('hidden_demo')}
          {allowPrivate ? <CopyButton value={u.mobile} phone what={t('phone', { defaultValue: 'Phone number' })} sx={{ my: -0.5 }} /> : null}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          <StatusChip
            tone="neutral"
            icon={isDriver ? <DirectionsCarOutlinedIcon /> : <PersonOutlineRoundedIcon />}
            label={isDriver ? L('driver', 'Driver') : L('customer', 'Customer')}
          />
          <StatusChip tone={approval.tone} icon={approval.icon} label={approval.label} />
          {isDriver ? (
            <StatusChip
              tone={online ? 'success' : 'neutral'}
              icon={
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{ width: 8, height: 8, borderRadius: '50%', ...(online ? { bgcolor: c.SUCCESS } : { border: `2px solid ${c.TEXT_3}` }) }}
                />
              }
              label={online ? L('drivers_filter_online', 'Online') : L('drivers_offline', 'Offline')}
            />
          ) : null}
          {isDriver && u.queue ? <StatusChip tone="info" icon={<RouteRoundedIcon />} label={L('drivers_state_on_trip', 'On a trip')} /> : null}
        </Box>
      </Box>
      {action ? <Box sx={{ marginInlineStart: 'auto' }}>{action}</Box> : null}
    </Paper>
  );
}

function UserInfoDetails(props) {
  const { data } = props;
  const { t, i18n } = useTranslation();
  const { fetchUsersOnce, fetchOtpAuthRequests } = api;
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.settingsdata.settings);
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const otpAuthData = useSelector((state) => state.otpauthdata);
  const auth = useSelector((state) => state.auth);
  const role = auth.profile && auth.profile.usertype ? auth.profile.usertype : null;
  const allowPrivate = !!settings.AllowCriticalEditsAdmin;

  // Agent names (fleet admins, call-centre staff, scouts) for the
  // "onboarded by" row, from the list already loaded (every staff role that
  // opens these pages; finance used to see only the raw ID).
  const fleetAdminsObj = useMemo(() => {
    if (!staticusers) return null;
    const obj = {};
    staticusers
      .filter((user) => user.usertype === 'fleetadmin' || user.usertype === 'callcenteragent' || user.usertype === 'callcentersupervisor' || user.usertype === 'scout')
      .forEach((user) => {
        obj[user.id] = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      });
    return obj;
  }, [staticusers]);

  useEffect(() => {
    dispatch(fetchUsersOnce());
  }, [dispatch, fetchUsersOnce]);

  // Fetch OTP auth requests when user data is available (only for specific roles)
  useEffect(() => {
    const allowedRoles = ['admin', 'callcentersupervisor'];
    if (data && data.mobile && settings.AllowCriticalEditsAdmin && role && allowedRoles.includes(role)) {
      dispatch(fetchOtpAuthRequests(data.mobile));
    }
  }, [data, dispatch, fetchOtpAuthRequests, settings.AllowCriticalEditsAdmin, role]);

  const hidden = t('hidden_demo');
  const noData = t('no_data_available', { defaultValue: 'No data available' });

  // Still loading the person (the details page passes [] until it has
  // them): the cards' shape in grey instead of a spinner over the page.
  if (!data || !data.id) {
    return (
      <Box role="status" aria-label={t('loading', { defaultValue: 'Loading…' })} sx={gridSx}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }} aria-hidden="true">
          <Skeleton variant="rounded" sx={{ width: '100%', maxWidth: 268, height: 220, borderRadius: '12px' }} />
          <Skeleton variant="rounded" width={160} height={14} />
          <Skeleton variant="rounded" width={120} height={14} />
        </Paper>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }} aria-hidden="true">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={`row-${i}`} variant="rounded" height={22} />
          ))}
        </Paper>
      </Box>
    );
  }

  const fullName = nameOf(data);
  const agentName = data.fleetadmin && fleetAdminsObj ? fleetAdminsObj[data.fleetadmin] : '';
  const agentTooltip = data.fleetadmin ? t('agent_id_tooltip', { id: allowPrivate ? data.fleetadmin : hidden, defaultValue: 'ID: {{id}}' }) : null;

  const otpValue = otpAuthData?.otpAuthRequest?.otp
    ? settings.AllowCriticalEditsAdmin
      ? `${otpAuthData.otpAuthRequest.otp}`
      : hidden
    : t('otp_no_active_code', { defaultValue: 'No active code' });

  const isDriver = data.usertype === 'driver';
  const isPerson = data.usertype === 'driver' || data.usertype === 'customer';
  const hasApp = !!(data.appVersion || data.appLastSeenAt);

  return (
    <Box dir={i18n.dir()} sx={gridSx}>
      {/* The photo and the account facts */}
      <Paper component="section" aria-label={t('profile_image', { defaultValue: 'Profile image' })} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
        {data.profile_image ? (
          <Box
            component="img"
            src={data.profile_image}
            alt={fullName || t('profile_image', { defaultValue: 'Profile image' })}
            sx={{ width: '100%', maxWidth: 268, aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: '12px', alignSelf: 'center', bgcolor: c.SURFACE_ALT }}
          />
        ) : (
          <Avatar
            variant="rounded"
            aria-hidden="true"
            sx={{ width: '100%', maxWidth: 268, height: 'auto', aspectRatio: '1 / 1', alignSelf: 'center', borderRadius: '12px', bgcolor: c.SURFACE_ALT, color: c.INK_2, fontSize: 56, fontWeight: 700 }}
          >
            {initialsOf(data) || <PersonOutlineRoundedIcon sx={{ fontSize: 96 }} />}
          </Avatar>
        )}
        {data.createdAt || hasApp ? (
          <Box component="dl" sx={{ m: 0 }}>
            {data.createdAt ? <InfoRow label={t('registered_on', { defaultValue: 'Member since' })}>{formatAddisDate(data.createdAt)}</InfoRow> : null}
            {hasApp ? (
              <InfoRow label={t('app_info', { defaultValue: 'App' })}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                  {data.appVersion ? (
                    <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {data.appVersion}
                      {data.appBuild ? ` (${data.appBuild})` : ''}
                    </Box>
                  ) : null}
                  {data.appPlatform ? (
                    <Chip
                      label={data.appPlatform === 'ios' ? 'iOS' : 'Android'}
                      size="small"
                      sx={{ ...toneChipSx(data.appPlatform === 'ios' ? 'info' : 'success'), height: 24, fontSize: 12 }}
                    />
                  ) : null}
                </Box>
              </InfoRow>
            ) : null}
            {data.appLastSeenAt ? <InfoRow label={t('last_active', { defaultValue: 'Last active' })}>{formatTimeAgo(t, data.appLastSeenAt)}</InfoRow> : null}
          </Box>
        ) : null}
      </Paper>

      <Box sx={{ display: 'grid', gap: 2, minWidth: 0, alignContent: 'start' }}>
        <DetailCard icon={<BadgeOutlinedIcon />} hue="info" title={t('profile', { defaultValue: 'Profile' })}>
          <InfoRow label={t('first_name')}>{data.firstName}</InfoRow>
          <InfoRow label={t('last_name')}>{data.lastName}</InfoRow>
          <InfoRow label={t('mobile')}>
            {!allowPrivate ? (
              hidden
            ) : data.mobile ? (
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontVariantNumeric: 'tabular-nums' }}>
                {formatPhoneDisplay(data.mobile)}
                <CopyButton value={data.mobile} phone what={t('phone', { defaultValue: 'Phone number' })} sx={{ my: -0.5 }} />
              </Box>
            ) : null}
          </InfoRow>
          <InfoRow label={t('email')}>{allowPrivate ? data.email : hidden}</InfoRow>
          {isDriver ? <InfoRow label={t('tin')}>{allowPrivate ? data.tin : hidden}</InfoRow> : null}
          {isDriver ? (
            <InfoRow label={t('commission_mode')}>
              {data.commissionMode === 'flat_daily'
                ? t('commission_flat_daily', { defaultValue: 'Daily flat rate' })
                : t('commission_per_trip', { defaultValue: 'Per-trip commission' })}
            </InfoRow>
          ) : null}
          <InfoRow label={t('verify_id')}>{data.verifyId ? (allowPrivate ? data.verifyId : hidden) : noData}</InfoRow>
          {role && ['admin', 'callcentersupervisor'].includes(role) ? (
            <InfoRow label={t('otp_login_code', { defaultValue: 'Login code (OTP)' })}>
              <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {otpValue}
              </Box>
            </InfoRow>
          ) : null}
          {isPerson && data.fleetadmin ? (
            <InfoRow label={calmCaps(t('fleetadmins'))}>
              <Tooltip title={agentTooltip}>
                <Box
                  component="span"
                  tabIndex={0}
                  sx={{ cursor: 'help', borderRadius: '6px', '&:focus-visible': { outline: `2px solid ${c.ACCENT}`, outlineOffset: 2 } }}
                >
                  {agentName || t('unknown', { defaultValue: 'Unknown' })}
                </Box>
              </Tooltip>
            </InfoRow>
          ) : null}
        </DetailCard>

        {isPerson ? (
          <DetailCard icon={<ContactsOutlinedIcon />} hue="accent" title={t('contact_invitation_info', { defaultValue: 'Contact invitation information' })}>
            <InfoRow label={t('invited_contacts')}>{data.hasInvitedContacts ? t('yes', { defaultValue: 'Yes' }) : t('no', { defaultValue: 'No' })}</InfoRow>
            <InfoRow label={t('total_contacts_invited')}>{data.totalContactsInvited || 0}</InfoRow>
            <InfoRow label={t('last_invite_date')}>{data.lastInviteDate ? formatAddisDateTime(data.lastInviteDate) : noData}</InfoRow>
            <InfoRow label={t('last_invite_count')}>{data.lastInviteCount || noData}</InfoRow>
          </DetailCard>
        ) : null}
      </Box>
    </Box>
  );
}

const gridSx = {
  display: 'grid',
  gap: 2,
  alignItems: 'start',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(240px, 300px) minmax(0, 1fr)' },
};

export default UserInfoDetails;
